import { HubSpotDeal, HubSpotCompany, HubSpotDealsResponse, DealWithCompany, DealSummary, AssociationLabel, DealStats, AssociationLabelOption, AssociationLabelsResponse } from '../types'

export interface LoadingProgress {
  phase: 'deals' | 'companies' | 'complete'
  current: number
  total: number
  message: string
}

export class HubSpotService {
  private static instance: HubSpotService
  private associationChanges: Map<string, string> = new Map() // dealId-companyId -> label
  private availableLabels: AssociationLabelOption[] = []
  private progressCallback?: (progress: LoadingProgress) => void
  
  // HubSpot account company to filter out
  private readonly ACCOUNT_COMPANY_NAMES = ['Enduro Finance', 'enduro finance', 'ENDURO FINANCE']
  
  static getInstance(): HubSpotService {
    if (!HubSpotService.instance) {
      HubSpotService.instance = new HubSpotService()
    }
    return HubSpotService.instance
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit = {}, includeAssociationLabels = false): Promise<Response> {
    try {
      // Use Netlify Function as proxy
      const netlifyFunctionUrl = '/.netlify/functions/hubspot-api'
      
      const response = await fetch(netlifyFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint,
          method: options.method || 'GET',
          body: options.body ? JSON.parse(options.body as string) : undefined,
          includeAssociationLabels
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(`HubSpot API error: ${errorData.error || response.statusText}`)
      }

      return response
    } catch (error) {
      console.error('Error calling HubSpot API:', error)
      // Fall back to mock data if API fails
      return this.getMockResponse(endpoint, options)
    }
  }

  private async getMockResponse(endpoint: string, options: RequestInit = {}): Promise<Response> {
    // Return mock data for development/testing
    console.warn('Using mock data - HubSpot API call failed')
    
    const mockDeals: HubSpotDeal[] = [
      {
        id: '1',
        properties: {
          dealname: 'Enduro Finance - Q1 2024 Funding',
          amount: '250000',
          closedate: '2024-03-15T00:00:00Z',
          hs_closed_won_date: '2024-03-15T00:00:00Z',
          dealstage: 'closedwon',
          pipeline: 'default',
          hs_object_id: '1',
          createdate: '2024-01-10T00:00:00Z',
          hs_lastmodifieddate: '2024-03-15T00:00:00Z',
          description: 'Q1 2024 funding round for Enduro Finance operations',
          funder_choice: 'Primary Funder'
        },
        associations: {
          companies: {
            results: [{ id: 'comp1', type: 'deal_to_company' }]
          }
        }
      },
      {
        id: '2',
        properties: {
          dealname: 'Enduro Finance - Equipment Lease',
          amount: '75000',
          closedate: '2024-02-28T00:00:00Z',
          hs_closed_won_date: '2024-02-28T00:00:00Z',
          dealstage: 'closedwon',
          pipeline: 'default',
          hs_object_id: '2',
          createdate: '2024-02-01T00:00:00Z',
          hs_lastmodifieddate: '2024-02-28T00:00:00Z',
          description: 'Equipment lease financing',
          funder_choice: 'Secondary Funder'
        },
        associations: {
          companies: {
            results: [{ id: 'comp1', type: 'deal_to_company' }]
          }
        }
      },
      {
        id: '3',
        properties: {
          dealname: 'Enduro Finance - Bridge Loan',
          amount: '150000',
          closedate: '2024-01-20T00:00:00Z',
          hs_closed_won_date: '2024-01-20T00:00:00Z',
          dealstage: 'closedwon',
          pipeline: 'default',
          hs_object_id: '3',
          createdate: '2024-01-05T00:00:00Z',
          hs_lastmodifieddate: '2024-01-20T00:00:00Z',
          description: 'Short-term bridge loan',
          funder_choice: 'Alternative Funder'
        },
        associations: {
          companies: {
            results: [{ id: 'comp1', type: 'deal_to_company' }]
          }
        }
      }
    ]

    const mockCompany: HubSpotCompany = {
      id: 'comp1',
      properties: {
        name: 'Enduro Finance',
        domain: 'endurofinance.com',
        industry: 'Financial Services',
        numberofemployees: '50',
        annualrevenue: '5000000',
        city: 'New York',
        state: 'NY',
        country: 'USA',
        type: 'Prospect'
      }
    }

    // Return appropriate mock data based on endpoint
    if (endpoint.includes('/companies/')) {
      return {
        ok: true,
        status: 200,
        statusText: 'OK',
        json: async () => mockCompany
      } as Response
    } else {
      return {
        ok: true,
        status: 200,
        statusText: 'OK',
        json: async () => ({ results: mockDeals })
      } as Response
    }
  }

  async fetchAvailableAssociationLabels(): Promise<AssociationLabelOption[]> {
    try {
      const response = await this.fetchWithAuth('/association-labels/deals/companies')
      const data: AssociationLabelsResponse = await response.json()
      const labelsFromApi = Array.isArray(data.results) ? data.results : []
      // Use API-provided mapping as the source of truth
      this.availableLabels = labelsFromApi
      console.log('Available association labels (from API):', this.availableLabels.map(l => `${l.label} (typeId: ${l.typeId}, category: ${l.category})`))
      return this.availableLabels
    } catch (error) {
      console.error('Error fetching association labels:', error)
      // Conservative fallback with minimal assumptions; avoid overriding API when it works
      this.availableLabels = []
      return this.availableLabels
    }
  }

  getAvailableLabels(): AssociationLabelOption[] {
    return this.availableLabels
  }

  getLabelOptions(): string[] {
    return this.availableLabels.map(label => label.label)
  }

  async getClosedWonDeals(maxResults: number = 500, onProgress?: (progress: LoadingProgress) => void): Promise<DealWithCompany[]> {
    this.progressCallback = onProgress
    
    // Clear any existing state
    this.associationChanges.clear()
    
    console.log('Starting to fetch closed won deals...')
    try {
      // Step 0: Ensure we have the available association labels for proper mapping
      if (this.availableLabels.length === 0) {
        await this.fetchAvailableAssociationLabels()
      }
      
      // Step 1: Fetch all closed won deals
      this.updateProgress('deals', 0, maxResults, 'Fetching closed won deals...')
      
      let allDeals: HubSpotDeal[] = []
      let after: string | undefined
      const limit = Math.min(100, maxResults) // HubSpot max per request is 100

      // Use search API with pagination to get all closed won deals
      do {
        this.updateProgress('deals', allDeals.length, maxResults, `Fetching deals... ${allDeals.length} found so far`)
        
        const searchEndpoint = '/crm/v3/objects/deals/search'
        const searchBody: any = {
          filterGroups: [
            {
              filters: [
                {
                  propertyName: 'dealstage',
                  operator: 'EQ',
                  value: 'closedwon'
                }
              ]
            }
          ],
          properties: ['dealname', 'amount', 'closedate', 'dealstage', 'pipeline', 'hs_closed_won_date', 'description', 'contract_type', 'contract_start_date', 'contract_end_date', 'funder_choice', 'product_type', 'value_of_commission_to_be_earned', 'rate', 'term'],
          limit: limit
        }

        // Add pagination token if we have one
        if (after) {
          searchBody.after = after
        }

        console.log(`Making HubSpot deals search request with limit: ${limit}, after: ${after || 'none'}`)
        const response = await this.fetchWithAuth(searchEndpoint, {
          method: 'POST',
          body: JSON.stringify(searchBody)
        })
        
        const dealsData: HubSpotDealsResponse = await response.json()
        
        if (dealsData.results) {
          allDeals = allDeals.concat(dealsData.results)
          console.log(`Fetched ${dealsData.results.length} deals, total so far: ${allDeals.length}`)
        }

        // Check if there are more results
        after = dealsData.paging?.next?.after
        
      } while (after && allDeals.length < maxResults)
      
      // De-duplicate by deal ID in case the API returns repeats across pages
      if (allDeals.length > 1) {
        const uniqueMap = new Map<string, HubSpotDeal>()
        for (const d of allDeals) uniqueMap.set(d.id, d)
        allDeals = Array.from(uniqueMap.values())
      }
      console.log(`Final count: ${allDeals.length} closed won deals`)
      
      if (allDeals.length === 0) {
        console.warn('No closed won deals found')
        return []
      }

      // Step 2: Batch fetch associations for all deals
      this.updateProgress('companies', 0, allDeals.length, 'Fetching deal associations...')
      
      const dealAssociations = await this.batchFetchDealAssociations(allDeals.map(d => d.id))
      
      // Detailed logging of association results
      const dealsWithAssocs = Object.entries(dealAssociations).filter(([_, assocs]) => assocs.length > 0)
      const dealsWithoutAssocs = Object.entries(dealAssociations).filter(([_, assocs]) => assocs.length === 0)
      
      console.log(`Association Summary:`)
      console.log(`  - Total deals: ${allDeals.length}`)
      console.log(`  - Deals with associations: ${dealsWithAssocs.length}`)
      console.log(`  - Deals without associations: ${dealsWithoutAssocs.length}`)
      
      if (dealsWithoutAssocs.length > 0 && dealsWithoutAssocs.length <= 10) {
        console.log(`  - Deals without associations: ${dealsWithoutAssocs.map(([id]) => id).join(', ')}`)
      }

      // Step 3: Get unique company IDs and batch fetch company details
      const uniqueCompanyIds = new Set<string>()
      let totalAssociations = 0
      
      Object.values(dealAssociations).forEach(associations => {
        associations.forEach(assoc => {
          if (assoc.toObjectId) {
            uniqueCompanyIds.add(assoc.toObjectId)
            totalAssociations++
          }
        })
      })
      
      const companyIds = Array.from(uniqueCompanyIds)
      console.log(`Found ${companyIds.length} unique companies from ${totalAssociations} total associations`)
      
      this.updateProgress('companies', 0, companyIds.length, 'Fetching company details...')
      
      const companies = await this.batchFetchCompanies(companyIds)
      console.log(`Successfully fetched ${Object.keys(companies).length}/${companyIds.length} companies`)

      // Step 4: Combine deals with their companies and associations
      this.updateProgress('companies', 0, allDeals.length, 'Processing deal-company relationships...')
      
      const dealsWithCompanies: DealWithCompany[] = []
      
      for (let i = 0; i < allDeals.length; i++) {
        const deal = allDeals[i]
        this.updateProgress('companies', i, allDeals.length, `Processing deal ${i + 1} of ${allDeals.length}`)
        
        const dealCompanies: HubSpotCompany[] = []
        const associations = dealAssociations[deal.id] || []
        
        for (const assoc of associations) {
          const companyId = assoc.toObjectId
          const company = companies[companyId]
          
          if (company && this.isValidCompany(company)) {
            // Extract association label - handle both label and typeId
            let associationLabel = 'Unlabeled'
            if (assoc.associationTypes && assoc.associationTypes.length > 0) {
              // Prefer USER_DEFINED association types when multiple are present; otherwise pick one with a non-empty label; else first
              const associationType = (() => {
                const types = assoc.associationTypes as any[]
                const userDefined = types.find(t => t?.category === 'USER_DEFINED')
                if (userDefined) return userDefined
                const labeled = types.find(t => t?.label)
                if (labeled) return labeled
                return types[0]
              })()
              
              // Prefer server-resolved label when provided by robust endpoint
              if ((assoc as any).resolvedLabel) {
                associationLabel = (assoc as any).resolvedLabel
              } else {
                // Prefer mapping by typeId to avoid relying on potentially inconsistent label strings
                const typeId = associationType.typeId
                const mapped = this.availableLabels.find(l => l.typeId === typeId)
                if (mapped) {
                  associationLabel = mapped.label
                  console.log(`Mapped typeId ${typeId} to label: ${associationLabel}`)
                } else if (associationType.label) {
                  // Fall back to label provided by API if no mapping exists
                  associationLabel = associationType.label
                } else {
                  console.log(`Unknown association typeId ${typeId} for deal ${deal.id}, company ${companyId}; defaulting to Unlabeled`)
                  associationLabel = 'Unlabeled'
                }
              }
              
              // Log the full association type for debugging
              console.log(`Deal ${deal.id}, Company ${companyId}: typeId=${associationType.typeId}, label=${associationType.label}, category=${associationType.category}, resolved=${associationLabel}`)
            }
            
            // Create company with association label
            const companyWithAssociation: HubSpotCompany = {
              ...company,
              associationLabel: this.getAssociationLabel(deal.id, companyId, associationLabel)
            }
            
            dealCompanies.push(companyWithAssociation)
            console.log(`Deal ${deal.id}: Added company ${company.properties.name} with final label: ${companyWithAssociation.associationLabel}`)
          } else if (company) {
            console.log(`Deal ${deal.id}: Filtered out account company ${company.properties.name}`)
          }
        }
        
        dealsWithCompanies.push({ deal, companies: dealCompanies })
      }

      this.updateProgress('complete', allDeals.length, allDeals.length, `Completed processing ${dealsWithCompanies.length} deals`)
      console.log(`Successfully processed ${dealsWithCompanies.length} deals with companies`)
      
      return dealsWithCompanies
    } catch (error) {
      console.error('Error fetching closed won deals:', error)
      throw error
    }
  }

  async searchDealsByAccount(accountName: string): Promise<DealWithCompany[]> {
    try {
      const searchEndpoint = '/crm/v3/objects/deals/search'
      const searchBody = {
        filterGroups: [
          {
            filters: [
              {
                propertyName: 'dealstage',
                operator: 'EQ',
                value: 'closedwon'
              }
            ]
          }
        ],
        properties: ['dealname', 'amount', 'closedate', 'dealstage', 'pipeline', 'hs_closed_won_date', 'funder_choice', 'product_type', 'value_of_commission_to_be_earned', 'rate', 'term'],
        associations: ['companies'],
        limit: 100
      }

      const response = await this.fetchWithAuth(searchEndpoint, {
        method: 'POST',
        body: JSON.stringify(searchBody)
      })
      
      const dealsData: HubSpotDealsResponse = await response.json()
      
      // Fetch company details and filter by account name
      const dealsWithCompanies: DealWithCompany[] = []
      
      for (const deal of dealsData.results) {
        if (deal.associations?.companies?.results?.[0]) {
          const companyId = deal.associations.companies.results[0].id
          try {
            const companyResponse = await this.fetchWithAuth(
              `/crm/v3/objects/companies/${companyId}?properties=name,domain,industry,type`
            )
            const company: HubSpotCompany = await companyResponse.json()
            
            // Filter by account name (case-insensitive partial match)
            if (company.properties.name?.toLowerCase().includes(accountName.toLowerCase())) {
              dealsWithCompanies.push({ deal, companies: [company] })
            }
          } catch (error) {
            console.error(`Error fetching company ${companyId}:`, error)
          }
        }
      }

      return dealsWithCompanies
    } catch (error) {
      console.error('Error searching deals by account:', error)
      throw error
    }
  }

  formatDealSummary(dealWithCompany: DealWithCompany): DealSummary {
    const { deal, companies } = dealWithCompany
    const amount = parseFloat(deal.properties.amount || '0')
    const commission = parseFloat(deal.properties.value_of_commission_to_be_earned || '0')
    const closeDate = new Date(deal.properties.hs_closed_won_date || deal.properties.closedate || '')

    return {
      id: deal.id,
      name: deal.properties.dealname || 'Unnamed Deal',
      amount,
      closeDate,
      stage: deal.properties.dealstage || '',
      pipeline: deal.properties.pipeline || '',
      funderChoice: deal.properties.funder_choice,
      productType: deal.properties.product_type,
      commission,
      rate: deal.properties.rate,
      term: deal.properties.term,
      companies: companies.map(company => ({
        id: company.id,
        name: company.properties.name,
        domain: company.properties.domain,
        type: company.properties.type,
        associationLabel: company.associationLabel
      })),
      formattedAmount: this.formatCurrency(amount),
      formattedDate: this.formatDate(closeDate),
      formattedCommission: this.formatCurrency(commission)
    }
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  private formatDate(date: Date): string {
    if (isNaN(date.getTime())) {
      return 'N/A'
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    })
  }

  updateAssociationLabel(dealId: string, companyId: string, newLabel: AssociationLabel): void {
    const key = `${dealId}-${companyId}`
    if (newLabel === 'Unlabeled') {
      this.associationChanges.delete(key)
    } else {
      this.associationChanges.set(key, newLabel)
    }
  }

  async saveAssociationToHubSpot(dealId: string, companyId: string, newLabel: AssociationLabel): Promise<{ success: boolean; message?: string }> {
    try {
      const netlifyFunctionUrl = '/.netlify/functions/hubspot-api'
      
      const response = await fetch(netlifyFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updateAssociation: {
            dealId,
            companyId,
            newLabel
          }
        })
      })

      const result = await response.json()
      
      if (result.success) {
        // Remove from local changes since it's now saved to HubSpot
        const key = `${dealId}-${companyId}`
        this.associationChanges.delete(key)
        return { success: true }
      } else {
        return { 
          success: false, 
          message: result.message || result.error || 'Failed to update association in HubSpot' 
        }
      }
    } catch (error) {
      console.error('Error saving association to HubSpot:', error)
      return { 
        success: false, 
        message: 'Network error: Failed to connect to HubSpot API' 
      }
    }
  }

  hasUnsavedChanges(dealId: string): boolean {
    return Array.from(this.associationChanges.keys()).some(key => key.startsWith(dealId))
  }

  getUnsavedChangesForDeal(dealId: string): Array<{ companyId: string; newLabel: string }> {
    const changes: Array<{ companyId: string; newLabel: string }> = []
    
    Array.from(this.associationChanges.entries()).forEach(([key, newLabel]) => {
      if (key.startsWith(dealId)) {
        const companyId = key.replace(`${dealId}-`, '')
        changes.push({ companyId, newLabel })
      }
    })
    
    return changes
  }

  getAssociationLabel(dealId: string, companyId: string, originalLabel?: string): string {
    const key = `${dealId}-${companyId}`
    return this.associationChanges.get(key) || originalLabel || 'Unlabeled'
  }

  calculateStats(deals: DealSummary[]): DealStats {
    const totalDeals = deals.length
    const totalAmount = deals.reduce((sum, deal) => sum + deal.amount, 0)
    const averageDealSize = totalDeals > 0 ? totalAmount / totalDeals : 0

    const dealsByMonth: Record<string, number> = {}
    const dealsByStage: Record<string, number> = {}
    const funderStats: Map<string, { value: number; dealCount: number }> = new Map()
    let dealsWithoutFunderCount = 0
    let dealsWithoutFunderValue = 0

    deals.forEach(deal => {
      // Group by month
      const monthKey = `${deal.closeDate.getFullYear()}-${String(deal.closeDate.getMonth() + 1).padStart(2, '0')}`
      dealsByMonth[monthKey] = (dealsByMonth[monthKey] || 0) + 1

      // Group by stage
      const stage = deal.stage || 'Unknown'
      dealsByStage[stage] = (dealsByStage[stage] || 0) + 1

      // Check if this deal has any funder companies
      let hasFunder = false

      // Calculate funder statistics - only count companies specifically labeled as "Funder"
      deal.companies.forEach(company => {
        const label = this.getAssociationLabel(deal.id, company.id, company.associationLabel)
        if (label === 'Funder') {
          hasFunder = true
          const existing = funderStats.get(company.name) || { value: 0, dealCount: 0 }
          funderStats.set(company.name, {
            value: existing.value + deal.amount,
            dealCount: existing.dealCount + 1
          })
        }
      })

      // Track deals without any funder
      if (!hasFunder) {
        dealsWithoutFunderCount++
        dealsWithoutFunderValue += deal.amount
      }
    })

    // Convert funder stats to arrays
    const fundersByValue = Array.from(funderStats.entries())
      .map(([name, stats]) => ({ name, value: stats.value, dealCount: stats.dealCount }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)

    const fundersByCount = Array.from(funderStats.entries())
      .map(([name, stats]) => ({ name, dealCount: stats.dealCount, totalValue: stats.value }))
      .sort((a, b) => b.dealCount - a.dealCount)
      .slice(0, 10)

    return {
      totalDeals,
      totalAmount,
      averageDealSize,
      dealsByMonth,
      dealsByStage,
      fundersByValue,
      fundersByCount,
      dealsWithoutFunder: {
        count: dealsWithoutFunderCount,
        totalValue: dealsWithoutFunderValue
      }
    }
  }

  // Helper method to update progress
  private updateProgress(phase: LoadingProgress['phase'], current: number, total: number, message: string): void {
    if (this.progressCallback) {
      this.progressCallback({ phase, current, total, message })
    }
  }

  // Helper method to validate company data and filter out account company
  private isValidCompany(company: HubSpotCompany): boolean {
    if (!company.properties?.name) {
      console.log('Invalid company: missing name')
      return false
    }
    
    const companyName = company.properties.name.trim()
    const isAccountCompany = this.ACCOUNT_COMPANY_NAMES.some(accountName => 
      companyName.toLowerCase() === accountName.toLowerCase()
    )
    
    if (isAccountCompany) {
      console.log(`Filtering out account company: ${companyName}`)
      return false
    }
    
    return true
  }


  // Helper method to batch fetch deal associations using HubSpot batch API with retry logic
  private async batchFetchDealAssociations(dealIds: string[]): Promise<Record<string, any[]>> {
    const aggregated: Record<string, any[]> = {}
    dealIds.forEach(id => (aggregated[id] = []))

    // Queue supports per-deal pagination; each entry may carry an `after` token
    type QueueItem = { id: string; after?: string }
    let queue: QueueItem[] = dealIds.map(id => ({ id }))

    const batchSize = 30
    const maxRetries = 3

    let batchIndex = 0
    while (queue.length > 0) {
      const batch = queue.slice(0, batchSize)
      queue = queue.slice(batchSize)
      batchIndex += 1

      console.log(`Fetching associations batch ${batchIndex}: ${batch.length} requests (queue remaining: ${queue.length})`)

      let retryCount = 0
      let success = false
      while (retryCount < maxRetries && !success) {
        try {
          const endpoint = '/crm/v4/associations/deals/companies/batch/read?includeAssociationTypes=true'
          const body = {
            inputs: batch.map(item => (item.after ? { id: item.id, after: item.after } : { id: item.id }))
          }
          const response = await this.fetchWithAuth(endpoint, { method: 'POST', body: JSON.stringify(body) })
          if (!response.ok) throw new Error(`API returned ${response.status}`)
          const data = await response.json()
          if (!data.results || !Array.isArray(data.results)) throw new Error('Invalid batch response structure')

          // Process results; merge pages, and re-queue items that have paging.next.after
          const responded = new Set<string>()
          for (const item of data.results) {
            const dealId = item?.from?.id
            if (!dealId) continue
            responded.add(dealId)

            const to = Array.isArray(item.to) ? item.to : []
            if (to.length > 0) {
              aggregated[dealId].push(...to)
              console.log(`Deal ${dealId}: +${to.length} associations (total ${aggregated[dealId].length})`)
            } else {
              console.log(`Deal ${dealId}: no associations on this page`)
            }

            const after = item?.paging?.next?.after
            if (after) {
              queue.push({ id: dealId, after })
            }
          }

          // Any deals in the batch that did not respond at all
          const missing = batch.filter(b => !responded.has(b.id)).map(b => b.id)
          if (missing.length > 0) console.warn(`Missing association data for ${missing.length} deals in batch: ${missing.join(', ')}`)

          success = true
        } catch (err) {
          retryCount += 1
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000)
          console.error(`Error in associations batch ${batchIndex} (attempt ${retryCount}/${maxRetries}):`, err)
          if (retryCount < maxRetries) {
            console.log(`Retrying after ${delay}ms...`)
            await this.sleep(delay)
          } else {
            console.error(`Giving up on associations batch ${batchIndex} after ${maxRetries} attempts`)
          }
        }
      }

      if (success && queue.length > 0) await this.sleep(200)
    }

    // Fallback per-deal fetch if still empty
    const stillEmpty = Object.entries(aggregated).filter(([_, v]) => v.length === 0).map(([id]) => id)
    if (stillEmpty.length > 0) {
      console.warn(`Server-side robust association fallback for ${stillEmpty.length} deals`)
      try {
        const resp = await this.fetchWithAuth('/associations/deals/companies/robust', {
          method: 'POST',
          body: JSON.stringify({ dealIds: stillEmpty })
        })
        if (resp.ok) {
          const data = await resp.json()
          const results = data?.results || {}
          let filled = 0
          for (const id of stillEmpty) {
            const arr = Array.isArray(results[id]) ? results[id] : []
            if (arr.length > 0) {
              aggregated[id].push(...arr)
              filled++
            }
          }
          console.log(`Robust endpoint filled ${filled}/${stillEmpty.length} previously empty deals`)
        } else {
          console.warn('Robust endpoint call failed, falling back to client per-deal fetch')
          throw new Error(`status ${resp.status}`)
        }
      } catch (e) {
        console.error('Robust endpoint failed:', e)
        // Fallback to client-side per-deal fetch as last resort
        for (let i = 0; i < stillEmpty.length; i++) {
          const id = stillEmpty[i]
          try {
            const pageAssocs = await this.fetchDealAssociationsSingle(id)
            if (pageAssocs.length > 0) aggregated[id].push(...pageAssocs)
            console.log(`Client fallback associations for deal ${id}: +${pageAssocs.length}`)
          } catch (err) {
            console.error(`Client fallback association fetch failed for deal ${id}:`, err)
          }
          if (i + 1 < stillEmpty.length) await this.sleep(100)
        }
      }
    }

    const withAssocs = Object.values(aggregated).filter(v => v.length > 0).length
    console.log(`Association fetch complete: ${withAssocs}/${dealIds.length} deals have associations`)
    return aggregated
  }

  // Helper method to batch fetch company details with retry logic
  private async batchFetchCompanies(companyIds: string[]): Promise<Record<string, HubSpotCompany>> {
    const result: Record<string, HubSpotCompany> = {}
    
    if (companyIds.length === 0) {
      console.log('No companies to fetch')
      return result
    }
    
    // Process in smaller batches for reliability
    const batchSize = 30 // Reduced from 100
    const maxRetries = 3
    
    for (let i = 0; i < companyIds.length; i += batchSize) {
      const batch = companyIds.slice(i, i + batchSize)
      console.log(`Fetching companies batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(companyIds.length/batchSize)}: ${batch.length} companies`)
      
      let retryCount = 0
      let success = false
      
      while (retryCount < maxRetries && !success) {
        try {
          const batchEndpoint = '/crm/v3/objects/companies/batch/read'
          const batchBody = {
            inputs: batch.map(companyId => ({ id: companyId })),
            properties: ['name', 'domain', 'industry', 'numberofemployees', 'annualrevenue', 'city', 'state', 'country', 'type']
          }
          
          const response = await this.fetchWithAuth(batchEndpoint, {
            method: 'POST',
            body: JSON.stringify(batchBody)
          })
          
          if (!response.ok) {
            throw new Error(`API returned ${response.status}`)
          }
          
          const batchData = await response.json()
          
          // Validate response
          if (!batchData.results || !Array.isArray(batchData.results)) {
            throw new Error('Invalid batch response structure')
          }
          
          console.log(`Batch ${Math.floor(i/batchSize) + 1}: Received ${batchData.results.length} companies for ${batch.length} requests`)
          
          // Track which companies we got
          const companiesReceived = new Set<string>()
          
          batchData.results.forEach((company: HubSpotCompany) => {
            if (company.id) {
              result[company.id] = company
              companiesReceived.add(company.id)
            }
          })
          
          // Check for missing companies
          const missingCompanies = batch.filter(id => !companiesReceived.has(id))
          if (missingCompanies.length > 0) {
            console.warn(`Missing data for ${missingCompanies.length} companies: ${missingCompanies.slice(0, 5).join(', ')}...`)
          }
          
          success = true
          
        } catch (error) {
          retryCount++
          console.error(`Error fetching companies batch ${Math.floor(i/batchSize) + 1} (attempt ${retryCount}/${maxRetries}):`, error)
          
          if (retryCount < maxRetries) {
            // Exponential backoff
            const delay = Math.min(1000 * Math.pow(2, retryCount), 5000)
            console.log(`Retrying after ${delay}ms...`)
            await this.sleep(delay)
          } else {
            console.error(`Failed to fetch companies for batch after ${maxRetries} attempts`)
          }
        }
      }
      
      // Delay between successful batches
      if (success && i + batchSize < companyIds.length) {
        await this.sleep(200)
      }
    }
    
    // Fallback: individually fetch any companies still missing after batch calls
    const missingCompanies = companyIds.filter(id => !result[id])
    if (missingCompanies.length > 0) {
      console.warn(`Attempting individual fetch for ${missingCompanies.length} missing companies`)
      const props = 'name,domain,industry,numberofemployees,annualrevenue,city,state,country,type'
      for (let i = 0; i < missingCompanies.length; i++) {
        const id = missingCompanies[i]
        try {
          const resp = await this.fetchWithAuth(`/crm/v3/objects/companies/${id}?properties=${encodeURIComponent(props)}`)
          if (resp.ok) {
            const comp = await resp.json()
            if (comp && comp.id) {
              result[id] = comp
              console.log(`Fetched missing company ${id} individually`)
            }
          }
        } catch (e) {
          console.error(`Failed to fetch missing company ${id}:`, e)
        }
        // Throttle a bit
        if (i + 1 < missingCompanies.length) await this.sleep(100)
      }
    }

    console.log(`Company fetch complete: ${Object.keys(result).length}/${companyIds.length} companies fetched`)
    
    return result
  }

  // Per-deal association fetch with pagination and association types included
  private async fetchDealAssociationsSingle(dealId: string): Promise<any[]> {
    const collected: any[] = []
    let after: string | undefined
    const limit = 100
    let page = 0
    
    while (true) {
      const qp: string[] = [
        `limit=${limit}`,
        'archived=false',
        'includeAssociationTypes=true'
      ]
      if (after) qp.push(`after=${encodeURIComponent(after)}`)
      const endpoint = `/crm/v4/objects/deals/${dealId}/associations/companies?${qp.join('&')}`
      const resp = await this.fetchWithAuth(endpoint)
      if (!resp.ok) break
      const data = await resp.json()
      if (Array.isArray(data.results)) {
        // Normalize into the same shape used by batch fetch (objects in `to` array)
        // Some responses may use `toObjectId` while others may have nested `to` structures; normalize to { toObjectId, associationTypes }
        for (const item of data.results) {
          if (item.toObjectId || item.id) {
            collected.push({
              toObjectId: item.toObjectId || item.id,
              associationTypes: item.associationTypes || []
            })
          } else if (item.to && item.to.id) {
            collected.push({
              toObjectId: item.to.id,
              associationTypes: item.associationTypes || []
            })
          }
        }
      }
      after = data.paging?.next?.after
      page += 1
      if (!after || page > 50) break
      // slight delay between pages
      await this.sleep(50)
    }
    return collected
  }

  // Helper method to add delays
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
